import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, Navigate } from "react-router-dom";
import { formatApiErrorDetail, istYear } from "@/lib/api";
import { Heart, LogIn, Loader2 } from "lucide-react";

export default function LoginPage() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (user && user.role) return <Navigate to={user.role === "admin" ? "/admin" : "/employee"} replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(username.trim(), password);
      nav(u.role === "admin" ? "/admin" : "/employee", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#F4F1EA] flex items-center justify-center p-4 sm:p-8" data-testid="login-page">
      <div className="w-full max-w-md rounded-3xl border border-[#E3DED4] bg-[#FDFBF7] p-6 sm:p-10 shadow-[0_20px_60px_rgba(45,49,46,0.10)]">
        <div className="w-full">
          <div className="mb-10">
            <div className="flex items-center gap-3 mb-8">
              <div className="h-10 w-10 rounded-2xl bg-[#4A5D4E] flex items-center justify-center">
                <Heart className="h-5 w-5 text-white" fill="currentColor" />
              </div>
              <div className="font-semibold text-lg" style={{ fontFamily: "Manrope" }}>Kaya Solutions</div>
            </div>
            <div className="text-overline mb-3">Sign in</div>
            <h2 className="text-3xl sm:text-4xl font-light text-[#2D312E]" style={{ fontFamily: "Manrope" }}>
              Welcome back.
            </h2>
            <p className="text-[#5C635E] mt-2">Access your donation tracking workspace.</p>
          </div>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="text-sm font-medium text-[#5C635E] mb-1.5 block">Username</label>
              <input
                data-testid="login-username-input"
                type="text"
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                placeholder="e.g. admin or your assigned username"
                className="w-full bg-[#FDFBF7] border border-[#EAE6DF] rounded-lg px-4 py-3 text-[#2D312E] placeholder-[#8A938D] focus:outline-none focus:ring-2 focus:ring-[#4A5D4E]/20 focus:border-[#4A5D4E] transition-all"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-[#5C635E] mb-1.5 block">Password</label>
              <input
                data-testid="login-password-input"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="Enter your password"
                className="w-full bg-[#FDFBF7] border border-[#EAE6DF] rounded-lg px-4 py-3 text-[#2D312E] placeholder-[#8A938D] focus:outline-none focus:ring-2 focus:ring-[#4A5D4E]/20 focus:border-[#4A5D4E] transition-all"
              />
            </div>

            {error && (
              <div data-testid="login-error" className="text-sm text-[#C05746] bg-[#C05746]/10 border border-[#C05746]/20 rounded-lg px-4 py-3">
                {error}
              </div>
            )}

            <button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="w-full bg-[#4A5D4E] hover:bg-[#3B4A3E] disabled:opacity-60 disabled:cursor-not-allowed text-white rounded-xl px-6 py-3.5 font-medium transition-all flex items-center justify-center gap-2 shadow-sm hover:shadow-md"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogIn className="h-4 w-4" />}
              {loading ? "Signing in…" : "Sign in"}
            </button>

            <div className="text-xs text-[#8A938D] text-center pt-4 leading-relaxed">
              By signing in you agree to allow location access for attendance logging.<br />
            </div>
          </form>
          <div className="text-center text-xs text-[#8A938D] mt-8">© {istYear()} Kaya Solutions. All rights reserved.</div>
        </div>
      </div>
    </div>
  );
}
